import {
  selectAiReviewStats,
  selectCanNavigateNext,
  selectCanNavigatePrevious,
  selectCurrentAnnotationIndex,
  selectIsStepByStepView,
  selectNextAnnotationId,
  selectPreviousAnnotationId,
} from "@/store/ai-asset-review/ai-asset-review-selectors";
import { setCurrentAnnotationId } from "@/store/ai-asset-review/ai-asset-review-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { grey } from "@faro-lotv/flat-ui";
import { Card, CardContent, Stack } from "@mui/material";
import { useCallback, type JSX } from "react";
import { AiNavigationCard } from "./ai-review-components/ai-navigation-card";
import { AiReviewStatusCard } from "./ai-review-components/ai-review-status-card";

/** @returns sidebar component with navigation controls and review statistics */
export function AiReviewSidebar(): JSX.Element {
  const dispatch = useAppDispatch();
  const isStepByStepView = useAppSelector(selectIsStepByStepView);
  const currentIndex = useAppSelector(selectCurrentAnnotationIndex);
  const canNavigateNext = useAppSelector(selectCanNavigateNext);
  const canNavigatePrevious = useAppSelector(selectCanNavigatePrevious);
  const { total } = useAppSelector(selectAiReviewStats);
  const previousAnnotationId = useAppSelector(selectPreviousAnnotationId);
  const nextAnnotationId = useAppSelector(selectNextAnnotationId);

  const handlePrevious = useCallback(() => {
    dispatch(setCurrentAnnotationId(previousAnnotationId));
  }, [dispatch, previousAnnotationId]);

  const handleNext = useCallback(() => {
    dispatch(setCurrentAnnotationId(nextAnnotationId));
  }, [dispatch, nextAnnotationId]);

  return (
    <Stack
      sx={{
        p: 2.5,
        height: "100%",
        minWidth: 300,
        maxWidth: 400,
        overflow: "auto",
      }}
    >
      {/* Navigation - only show in step-by-step mode */}
      {isStepByStepView && (
        <Card
          sx={{
            width: "100%",
            border: `0.5px solid ${grey[200]}`,
            boxShadow: "none",
            borderRadius: 0,
            mb: 2.5,
            flexShrink: 0,
          }}
        >
          <CardContent sx={{ p: "0.75rem", "&:last-child": { pb: "0.75rem" } }}>
            <AiNavigationCard
              currentIndex={currentIndex}
              total={total}
              canNavigatePrevious={canNavigatePrevious}
              canNavigateNext={canNavigateNext}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </CardContent>
        </Card>
      )}

      {/* Review Status Card */}
      <Card
        sx={{
          width: "100%",
          border: `0.5px solid ${grey[200]}`,
          boxShadow: "none",
          borderRadius: 0,
          flex: 1,
        }}
      >
        <CardContent sx={{ p: "0.75rem" }}>
          <AiReviewStatusCard />
        </CardContent>
      </Card>
    </Stack>
  );
}
